import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useSession } from '@/features/auth/use-session'
import { updateLanguagePreference } from './api'
import { LanguageContext } from './language-context'
import { messagesFor } from './messages'
import { negotiateLanguage } from './negotiate'
import { isLanguageId, type LanguageId } from './types'

const STORAGE_KEY = 'mlog.language'

/**
 * 브라우저에 남는 것은 사용자가 명시로 고른 값 하나다. 협상 결과를 저장하지 않는다. 저장하면
 * 다음 방문에 협상 결과가 사용자 선택인 척 서버로 올라간다.
 */
function readStoredLanguage(): LanguageId | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLanguageId(stored) ? stored : null
  } catch {
    return null
  }
}

function readNegotiatedLanguage(): LanguageId {
  return negotiateLanguage(navigator.languages ?? [navigator.language])
}

/**
 * 화면 언어를 세우고 문구 묶음을 내려보낸다.
 *
 * 값을 고르는 자리가 넷이라 앞뒤를 정한다. 이번 방문에서 고른 값이 가장 앞서고, 없으면 계정에
 * 남은 값을, 그것도 없으면 이 브라우저에 남은 명시 선택을, 마지막으로 브라우저가 말하는 선호
 * 언어를 지원 목록과 맞춘 결과를 쓴다. 어디에도 걸리지 않으면 한국어다.
 *
 * 계정 값이 이번 방문의 선택을 덮지 않는다. 화면을 옮길 때마다 서버 값이 다시 오는데 덮게 두면
 * 방금 고른 언어가 되돌아간다. 테마가 같은 구조로 서 있다.
 *
 * 언어를 바꿔도 화면을 다시 세우지 않는다. 문구 묶음을 갈아 끼워 그 자리에서 다시 그린다.
 * 스크롤 위치와 포커스가 남고 방금 누른 결과를 같은 화면에서 본다.
 *
 * 로그인 전에 고른 값을 계정으로 올리는 길은 두지 않았다. 언어를 고르는 자리가 설정 하나이고
 * 설정은 인증 뒤에 있어 로그인 전 선택이 일어나지 않는다. 그 자리를 문 앞에 열게 되면 올리는
 * 길을 함께 세운다.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const session = useSession()
  const [storedLanguage] = useState(readStoredLanguage)
  const [negotiatedLanguage] = useState(readNegotiatedLanguage)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageId | null>(null)
  const [cleared, setCleared] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  const { mutate: saveLanguage } = useMutation({
    mutationFn: updateLanguagePreference,
    onSuccess: () => setSaveFailed(false),
    onError: () => setSaveFailed(true),
  })

  /*
   * 고른 값과 화면이 선 언어를 가른다. 시스템 설정을 고른 사람은 고른 값이 없는 상태로
   * 돌아가지만 화면은 여전히 어떤 언어로든 서 있어야 한다.
   *
   * 이번 방문에 시스템 설정을 골랐으면 계정과 저장소의 값을 무시한다. 그 둘을 비우는
   * 요청이 아직 돌고 있거나 실패했더라도 방금 누른 결과가 화면에 서야 한다.
   */
  const chosenLanguage = cleared
    ? null
    : (selectedLanguage ?? session.preferences?.languageId ?? storedLanguage)

  const languageId = chosenLanguage ?? negotiatedLanguage

  /*
   * 화면 낭독기가 이 값으로 발음을 고르고 줄바꿈 규칙과 CJK 글꼴 폴백도 이것을 본다. 문서가
   * 그려지기 전 첫 값은 머리의 스크립트가 세우고 여기서는 바뀔 때마다 따라간다.
   */
  useEffect(() => {
    document.documentElement.lang = languageId
  }, [languageId])

  const chooseLanguage = useCallback(
    (next: LanguageId | null) => {
      setSelectedLanguage(next)
      setCleared(next === null)
      setSaveFailed(false)
      try {
        if (next === null) localStorage.removeItem(STORAGE_KEY)
        else localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // 저장소를 막아 둔 브라우저가 있다. 이번 방문의 선택은 그대로 살아 있다.
      }
      if (session.status === 'authenticated') {
        saveLanguage({ languageId: next })
      }
    },
    [saveLanguage, session.status],
  )

  const value = useMemo(
    () => ({
      languageId,
      messages: messagesFor(languageId),
      chosenLanguage,
      chooseLanguage,
      saveFailed,
    }),
    [languageId, chosenLanguage, chooseLanguage, saveFailed],
  )

  return <LanguageContext value={value}>{children}</LanguageContext>
}
